/**
 * TypeScript mirrors of the Pydantic response models in
 * `src/api/models/meta_campaign.py`. Read-only A3 shapes — A4 will add
 * Create/Update request types.
 *
 * The shared `MetaMetrics` shape mirrors `MetaCampaignBase`'s metrics
 * block. `conversions_value` is the Google-compatible alias for Meta's
 * `purchases_value` so the unified Account-page ROAS calc
 * (conversions_value / cost) works across both platforms without
 * platform-specific branches.
 */

export interface MetaMetrics {
  impressions: number;
  clicks: number;
  cost: number;
  reach: number;
  purchases: number;
  purchases_value: number;
  conversions_value: number;
  ctr: number;
  cpc: number;
  cpm: number;
}

export interface MetaAd {
  ad_id: string;
  ad_name: string | null;
  status: string | null;
  effective_status: string | null;
  creative_id: string | null;
  metrics: MetaMetrics;
}

export interface MetaAdList {
  ads: MetaAd[];
  total: number;
}

export interface MetaAdSetSummary {
  ad_set_id: string;
  ad_set_name: string | null;
  status: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  billing_event: string | null;
  optimization_goal: string | null;
  metrics: MetaMetrics;
}

export interface MetaAdSet extends MetaAdSetSummary {
  bid_amount: number | null;
  start_time: string | null;
  end_time: string | null;
  /**
   * Pass-through of the Meta Insights `targeting` JSONB. The shape is
   * platform-defined and varies by campaign type; `TargetingPanel`
   * pretty-prints rather than typing it deeply.
   */
  targeting: Record<string, unknown>;
  /**
   * Pass-through of the Meta `promoted_object`. Only populated on ad sets
   * created via Flouri's wizard (LEADS / SALES / APP_PROMOTION); null for
   * pre-existing ad sets ingested from Meta. The ad creation wizard reads
   * `promoted_object.page_id` here to inherit the Page for LEADS ads.
   */
  promoted_object: Record<string, unknown> | null;
  /**
   * Pass-through of the Meta `destination_type`. For LEADS: `ON_AD` means
   * instant form (required for `lead_gen_form_id`-attached creatives).
   * Null on ad sets that predate persistence.
   */
  destination_type: string | null;
  ads: MetaAd[];
}

export interface MetaAdSetList {
  ad_sets: MetaAdSet[];
  total: number;
}

/**
 * Discriminator used by the Account page to distinguish Meta entries
 * from Google ones in the unified grid. Set by `metaCampaignsApi`
 * after fetching — the backend's `platform_source` is `'Meta Ads'`,
 * mirrored here.
 */
export const META_PLATFORM_SOURCE = 'Meta Ads' as const;
export type MetaPlatformSource = typeof META_PLATFORM_SOURCE;

export interface MetaCampaign {
  campaign_id: string;
  campaign_name: string;
  campaign_status: string | null;
  objective: string | null;
  daily_budget: number | null;
  lifetime_budget: number | null;
  special_ad_categories: string[];
  bid_strategy: string | null;
  meta_ad_account_id: string | null;
  buying_type: string | null;
  is_cbo: boolean;
  metrics: MetaMetrics;
  flouri_optimize_budget: boolean;
  start_time: string | null;
  stop_time: string | null;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Client-side tag set by `metaCampaignsApi` so the Account page can
   * dispatch render targets without a separate type predicate.
   */
  platform_source: MetaPlatformSource;
}

export interface MetaCampaignList {
  campaigns: MetaCampaign[];
  total: number;
}

export interface MetaCampaignDetail extends MetaCampaign {
  ad_sets: MetaAdSetSummary[];
}
